import React from "react";
import { Box, Button, Flex, Input, Spinner, Stack, Text } from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import { FiBookOpen, FiCheck, FiMapPin, FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useCampus } from "features/campus/hooks";
import { usePerfilSettings } from "features/perfil/settings/store";
import { useUpdateMiFamilia } from "features/perfil/familia/hooks";
import { useMisHijos, useUpdateHijo } from "features/perfil/hijos/hooks";
import FamiliaEditorLayout, { EditorField, editorInputProps } from "./FamiliaEditorLayout";

const MotionBox = motion.create(Box);
const SCHOOLS = ["Liceo Blueridge", "North Hill", "Ciudad de los Ni\u00f1os"];

export default function EditarEscolarPerfil() {
  const navigate = useNavigate();
  const perfil = usePerfilSettings();
  const { data: campuses = [], isLoading: campusLoading } = useCampus();
  const { data: children = [], isLoading: childrenLoading } = useMisHijos(perfil.ready);
  const updateFamily = useUpdateMiFamilia();
  const updateChild = useUpdateHijo();
  const [form, setForm] = React.useState({ campusId: "", colegio: "" });
  const [customSchool, setCustomSchool] = React.useState(false);
  const [error, setError] = React.useState("");
  const initialized = React.useRef(false);
  const loading = !perfil.ready || campusLoading || childrenLoading;
  const saving = updateFamily.isPending || updateChild.isPending;

  React.useEffect(() => {
    if (loading || initialized.current) return;
    const colegio = perfil.familia?.colegio || children[0]?.colegio || "";
    setForm({ campusId: perfil.familia?.campusId || children[0]?.campusId || campuses[0]?.id || "", colegio });
    setCustomSchool(Boolean(colegio && !SCHOOLS.includes(colegio)));
    initialized.current = true;
  }, [loading, perfil.familia, children, campuses]);

  if (loading) return <Box minH="100dvh" display="grid" placeItems="center"><Spinner color="brand.red"/></Box>;

  const valid = Boolean(form.campusId && form.colegio.trim());
  const save = async () => {
    setError("");
    try {
      for (const child of children) {
        await updateChild.mutateAsync({ id: child.id, patch: { campusId: form.campusId, generacion: Number(child.generacion), colegio: form.colegio.trim() } });
      }
      await updateFamily.mutateAsync({ campusId: form.campusId, colegio: form.colegio.trim() });
      await Promise.all([perfil.refetchContext(), perfil.invalidateHijos()]);
      navigate("/perfil/familia", { replace: true });
    } catch { setError("No pudimos actualizar los datos escolares de toda la familia."); }
  };

  return <FamiliaEditorLayout title="Datos escolares" description={"Una sola configuraci\u00f3n para todos tus hijos"} icon={<FiBookOpen/>} onSave={save} disabled={!valid} busy={saving}>
    <Stack gap="8" maxW="570px">
      <EditorField label="Campus">
        <Stack gap="3">{campuses.slice(0, 3).map((campus) => {
          const active = form.campusId === campus.id;
          return <Button key={campus.id} h="64px" px="4" rounded="18px" justifyContent="flex-start" bg={active ? "red.50" : "public.bg.surface"} borderWidth="1px" borderColor={active ? "red.300" : "public.border.subtle"} color={active ? "red.700" : "public.fg.default"} _dark={{ bg: active ? "red.950" : "public.bg.surface", borderColor: active ? "red.800" : "public.border.subtle", color: active ? "red.200" : "public.fg.default" }} onClick={() => setForm((current) => ({ ...current, campusId: campus.id }))}>
            <Flex boxSize="38px" rounded="13px" bg={active ? "red.600" : "public.bg.muted"} color={active ? "gray.50" : "public.fg.muted"} align="center" justify="center" fontSize="18px"><FiMapPin/></Flex>
            <Text ml="3" fontWeight="800">{campus.nombre}</Text>
            {active && <Flex ml="auto" boxSize="24px" rounded="full" bg="red.600" color="gray.50" align="center" justify="center"><FiCheck/></Flex>}
          </Button>;
        })}</Stack>
      </EditorField>

      <EditorField label="Colegio" hint={"Este nombre se usará para todos tus hijos."}>
        <Stack gap="2">{SCHOOLS.map((school) => {
          const active = !customSchool && form.colegio === school;
          return <Button key={school} h="54px" px="4" rounded="16px" justifyContent="flex-start" bg={active ? "red.50" : "public.bg.surface"} color={active ? "red.700" : "public.fg.default"} borderWidth="1px" borderColor={active ? "red.300" : "public.border.subtle"} _dark={{ bg: active ? "red.950" : "public.bg.surface", borderColor: active ? "red.800" : "public.border.subtle", color: active ? "red.200" : "public.fg.default" }} onClick={() => { setCustomSchool(false); setForm((current) => ({ ...current, colegio: school })); }}>
            <Flex boxSize="26px" rounded="full" bg={active ? "red.600" : "public.bg.muted"} color="gray.50" align="center" justify="center">{active && <FiCheck/>}</Flex>
            <Text ml="3" fontWeight="700">{school}</Text>
          </Button>;
        })}
          <Button h="48px" justifyContent="flex-start" variant="ghost" color="gray.600" onClick={() => { setCustomSchool(true); if (SCHOOLS.includes(form.colegio)) setForm((current) => ({ ...current, colegio: "" })); }}><FiPlus/>{"Mi colegio no está en la lista"}</Button>
          <AnimatePresence>{customSchool && <MotionBox initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}><Input {...editorInputProps} autoFocus placeholder="Nombre completo del colegio" value={form.colegio} onChange={(event) => setForm((current) => ({ ...current, colegio: event.target.value }))}/></MotionBox>}</AnimatePresence>
        </Stack>
      </EditorField>

      <Box p="4" rounded="18px" bg="blue.50" color="blue.800"><Text fontWeight="800">{"Se aplicará a"} {children.length || "todos los"} {children.length === 1 ? "hijo" : "hijos"}</Text><Text mt="1" fontSize="sm">{"Ya no tendrás que actualizar cada perfil por separado."}</Text></Box>
      {error && <Text color="red.600" fontSize="sm">{error}</Text>}
    </Stack>
  </FamiliaEditorLayout>;
}
